import { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Plus, RefreshCw, FileDown } from 'lucide-react';
import { DeviceTable, type InventoryViewTab, type StatusChipOption } from '@/components/inventory/DeviceTable';
import { deviceBucket, hasAssignedOwner, isReadyToDeliver } from '../../../../shared/inventory-status';
import { DeviceFormDialog } from '@/components/inventory/DeviceFormDialog';
import { InventoryMetrics } from '@/components/inventory/InventoryMetrics';
import { QuipteamsSuggestionsTab } from '@/components/inventory/QuipteamsSuggestionsTab';
import { QuipteamsSupportTab } from '@/components/inventory/QuipteamsSupportTab';
import { Badge } from '@/components/ui/badge';
import { useDevices, useDeleteDevice, useHardDeleteDevice } from '@/hooks/useDevices';
import { trpc } from '@/lib/trpc';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import type { Device, DeviceFilters } from '../../../../shared/types';
import { downloadXlsx } from '@/lib/xlsx';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export function InventoryPage() {
  const { t } = useTranslation();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | undefined>();
  const [filters, setFilters] = useState<DeviceFilters>({});
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('inventory');
  // Tres cajones derivados del status (Sol 15/09): Activo = Entregado + Lista
  // para Entregar; Intermedio = el taller (Localizar / Pending / Recupero);
  // Inactivo = bajas. Cambiar el status mueve el equipo de tab solo. El chip
  // seleccionado es una clave: el status trimmed, o 'Lista para Entregar|assigned'
  // / '|unassigned' para distinguir las que ya tienen destinatario.
  const [viewTab, setViewTab] = useState<InventoryViewTab>('active');
  const [statusChip, setStatusChip] = useState<string | null>(null);

  const { data: devices = [], isLoading, refetch } = useDevices(filters);

  const { tabCounts, statusCounts, visibleDevices } = useMemo(() => {
    const active = devices.filter((d) => deviceBucket(d.status) === 'active');
    const intermediate = devices.filter((d) => deviceBucket(d.status) === 'intermediate');
    const inactive = devices.filter((d) => deviceBucket(d.status) === 'inactive');
    const inTab =
      viewTab === 'active' ? active
      : viewTab === 'intermediate' ? intermediate
      : viewTab === 'inactive' ? inactive
      : devices;

    // Conteo por status TRIMMED ('Localizar ' y 'Localizar' = un solo chip);
    // "Lista para Entregar" se abre en asignadas / sin asignar.
    const chipKey = (d: Device) => {
      const s = (d.status || '').trim() || '—';
      return isReadyToDeliver(s) ? `${s}|${hasAssignedOwner(d.owner) ? 'assigned' : 'unassigned'}` : s;
    };
    const counts = new Map<string, { status: string; assigned?: boolean; count: number }>();
    for (const d of inTab) {
      const key = chipKey(d);
      const cur = counts.get(key);
      if (cur) cur.count += 1;
      else {
        const s = (d.status || '').trim() || '—';
        counts.set(key, { status: s, assigned: isReadyToDeliver(s) ? hasAssignedOwner(d.owner) : undefined, count: 1 });
      }
    }
    const statusCounts: StatusChipOption[] = [...counts.entries()]
      .map(([key, v]) => ({ key, ...v }))
      .sort((a, b) => b.count - a.count || a.key.localeCompare(b.key));

    const visibleDevices = statusChip ? inTab.filter((d) => chipKey(d) === statusChip) : inTab;

    return {
      tabCounts: {
        active: active.length,
        intermediate: intermediate.length,
        inactive: inactive.length,
        all: devices.length,
      },
      statusCounts,
      visibleDevices,
    };
  }, [devices, viewTab, statusChip]);

  const handleViewTabChange = (tab: InventoryViewTab) => {
    setViewTab(tab);
    setStatusChip(null); // el chip pertenece a la vista anterior
  };
  const deleteDevice = useDeleteDevice();
  const hardDeleteDevice = useHardDeleteDevice();

  // QuipTeams suggestions: fetched on-demand when the tab is opened
  const quipteamsQuery = trpc.inventory.getQuipteamsSuggestions.useQuery(undefined, {
    enabled: activeTab === 'quipteams',
  });
  const suggestions = quipteamsQuery.data?.suggestions ?? [];

  const syncABM = trpc.inventory.syncABM.useMutation({
    onSuccess: (s) => {
      toast.success(
        t('devices.inventory_page.abm_synced', {
          updated: s.columnUpdated + s.markedNoAplica,
          errors: s.errors,
        })
      );
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const handleAdd = () => {
    setSelectedDevice(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = (device: Device) => {
    setSelectedDevice(device);
    setIsFormOpen(true);
  };

  const handleDelete = (deviceId: string) => {
    setDeleteTarget(deviceId);
  };

  const handleSoftDelete = async () => {
    if (!deleteTarget) return;
    await deleteDevice.mutateAsync(deleteTarget);
    setDeleteTarget(null);
  };

  const handleHardDelete = async () => {
    if (!deleteTarget) return;
    await hardDeleteDevice.mutateAsync(deleteTarget);
    setDeleteTarget(null);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setSelectedDevice(undefined);
  };

  const handleExportToExcel = async () => {
    try {
      // Exporta la VISTA actual (tab activo/inactivo + chip + filtros + search)
      const excelData = visibleDevices.map((device) => ({
        'Fecha Compra': device.fechaCompra || '-',
        'Proveedor': device.proveedor || '-',
        'Num. Factura': device.numFactura || '-',
        'Costo': device.costo || '-',
        'ClapBase': device.clapBase,
        'Referencia': device.nroReferencia,
        'Categoría': device.categoria,
        'Modelo': device.modelo,
        'Nro de Modelo': device.nroModelo || '-',
        'Serial': device.nroSerie,
        'Procesador': device.procesador || '-',
        'RAM (GB)': device.ramGB || '-',
        'Disco (GB)': device.discoGB || '-',
        'ABM': device.abm || '-',
        'Status': device.status,
        'Propietario': device.owner || '-',
        'Comentarios': device.comments || '-',
      }));

      // Descargar archivo (helper compartido lib/xlsx.ts: guard anti-fórmula + anchos)
      const fileName = `inventario_${new Date().toISOString().split('T')[0]}.xlsx`;
      await downloadXlsx(fileName, Object.keys(excelData[0] ?? {}), excelData.map((r) => Object.values(r)), {
        sheet: 'Inventario',
        colWidths: [12, 20, 15, 10, 15, 12, 15, 25, 15, 20, 20, 10, 10, 15, 15, 25, 30],
      });

      toast.success(t('devices.toasts.export_success', { fileName }));
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error(t('devices.toasts.export_error'));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">{t('devices.inventory_page.title')}</h2>
          <p className="text-muted-foreground">
            {t('devices.inventory_page.header_counts', {
              active: tabCounts.active,
              intermediate: tabCounts.intermediate,
              inactive: tabCounts.inactive,
              total: tabCounts.all,
            })}
          </p>
        </div>

        {/* Acciones solo donde aplican (tab Inventario) */}
        {activeTab === 'inventory' && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportToExcel}>
              <FileDown className="mr-2 h-4 w-4" />
              {t('devices.inventory_page.export_view', { count: visibleDevices.length })}
            </Button>
            <Button variant="outline" onClick={() => syncABM.mutate()} disabled={syncABM.isPending}>
              {syncABM.isPending ? (
                <WinclapLoader size={16} className="mr-2" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              {t('devices.inventory_page.sync_abm')}
            </Button>
            <Button onClick={handleAdd}>
              <Plus className="mr-2 h-4 w-4" />
              {t('devices.inventory_page.new_device')}
            </Button>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="inventory">{t('devices.inventory_page.tab_inventory')}</TabsTrigger>
          <TabsTrigger value="quipteams" className="gap-2">
            {t('devices.inventory_page.tab_quipteams')}
            {suggestions.length > 0 && (
              <Badge className="h-5 min-w-5 justify-center rounded-full bg-primary px-1.5 text-xs">
                {suggestions.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="support">{t('devices.inventory_page.tab_support')}</TabsTrigger>
          <TabsTrigger value="metrics">{t('devices.inventory_page.tab_metrics')}</TabsTrigger>
        </TabsList>

        <TabsContent value="inventory" className="mt-4">
          {/* Tabla con filtros integrados */}
          <Card>
            <DeviceTable
              devices={visibleDevices}
              isLoading={isLoading}
              onEdit={handleEdit}
              onDelete={handleDelete}
              filters={filters}
              onFiltersChange={setFilters}
              viewTab={viewTab}
              onViewTabChange={handleViewTabChange}
              tabCounts={tabCounts}
              statusCounts={statusCounts}
              statusChip={statusChip}
              onStatusChipChange={setStatusChip}
            />
          </Card>
        </TabsContent>

        <TabsContent value="quipteams" className="mt-4">
          <QuipteamsSuggestionsTab
            suggestions={suggestions}
            isLoading={quipteamsQuery.isLoading}
            error={quipteamsQuery.data?.error ?? null}
            onAdded={() => {
              quipteamsQuery.refetch();
              refetch();
            }}
          />
        </TabsContent>

        <TabsContent value="support" className="mt-4">
          <QuipteamsSupportTab />
        </TabsContent>

        <TabsContent value="metrics" className="mt-4">
          <InventoryMetrics />
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('devices.delete_dialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('devices.delete_dialog.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>{t('devices.delete_dialog.cancel')}</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={handleSoftDelete}
              disabled={deleteDevice.isPending}
            >
              {t('devices.delete_dialog.soft_delete')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleHardDelete}
              disabled={hardDeleteDevice.isPending}
            >
              {t('devices.delete_dialog.hard_delete')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modals */}
      <DeviceFormDialog open={isFormOpen} onOpenChange={handleFormClose} device={selectedDevice} />
    </div>
  );
}
